import NiceModal from "@ebay/nice-modal-react";
import clsx from "clsx";
import { endOfMonth } from "date-fns";
import { useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useRecoilValue } from "recoil";

import Board from "components/Board";
import Box from "components/Box";
import Button from "components/Button";
import Label from "components/Label";
import IncomePreviewModal from "components/modals/IncomePreviewModal";
import PasswordTextField from "components/PasswordTextField";
import Select from "components/Select";
import TextField from "components/TextField";
import TitleHeading from "components/TitleHeading";
import { useGetMyInfo, useUpdateMyInfo } from "hooks/apis/auth";
import { useCheckPassword } from "hooks/apis/user";
import format from "lib/format";
import { userState } from "store/store";
import { bankOptions } from "utils/const";

type FormValues = {
  name: string;
  userId: string;
  currentPassword?: string;
  password?: string;
  passwordConfirm?: string;
  phone: string;
  daesungId: string;
  bank: string;
  bankNumber: string;
};

const MyInfoPage = () => {
  const user = useRecoilValue(userState);
  const { data } = useGetMyInfo();

  const methods = useForm<FormValues>({ mode: "onChange" });
  const password = methods.watch("password");

  const updateMyInfoMutation = useUpdateMyInfo();
  const checkPasswordMutation = useCheckPassword();

  useEffect(() => {
    if (user) {
      methods.reset({
        name: user.name,
        userId: user.userId,
        phone: user.phone,
        daesungId: user.daesungId,
        bank: user.bank,
        bankNumber: user.bankNumber,
      });
    }
  }, [user]);

  const onSubmit = (form: FormValues) => {
    updateMyInfoMutation.mutate(form);
  };

  const onPreview = (answerType: number) => {
    NiceModal.show(IncomePreviewModal, { id: user!.id, answerType });
  };

  const values = useMemo(
    () => [
      {
        label: "총 답변 수",
        value: (
          (data?.noBonusCount ?? 0) +
          (data?.bonusCount ?? 0) +
          (data?.reportLossCount ?? 0) +
          ([1, 2].includes(user!.grade.id) ? (data?.reportTakenCount ?? 0) : 0)
        ).toLocaleString(),
        unit: "건",
        className: "text-black",
      },
      {
        label: "기본 답변 수 ",
        value: `${(data?.noBonusCount ?? 0).toLocaleString?.()}`,
        unit: "건",
        description: data?.noBonusSum ? `+${data.noBonusSum}원` : undefined,
        className: "text-black",
        onClick: () => onPreview(0),
      },
      // {
      //   label: "보너스 답변 수",
      //   value: `${(data?.bonusCount ?? 0)?.toLocaleString?.()}`,
      //   unit: "건",
      //   description: data?.bonusSum
      //     ? `+${data.bonusSum.toLocaleString()}원`
      //     : undefined,
      //   className: "text-black",
      //   onClick: () => onPreview(1),
      // },
      {
        label: "경고 수",
        value: `${(data?.reportLossCount ?? 0)?.toLocaleString?.()}`,
        unit: "건",
        className: "text-red after:!bg-red",
        onClick: () => onPreview(2),
      },
      ...([1, 2].includes(user!.grade.id)
        ? [
            {
              label: "재답변 수",
              value: `${(data?.reportTakenCount ?? 0)?.toLocaleString?.()}`,
              unit: "건",
              description: data?.reportTakenSum
                ? `+${data.reportTakenSum}원`
                : undefined,
              className: "text-black",
              onClick: () => onPreview(3),
            },
          ]
        : []),
      {
        label: "예상 급여",
        value: (data
          ? +(data.noBonusSum ?? 0) +
            +(data.bonusSum ?? 0) +
            +(data.reportTakenSum ?? 0)
          : 0
        ).toLocaleString(),
        unit: "원",
      },
    ],
    [user, data],
  );

  return (
    <main className="py-[1.875rem] px-[3.125rem]">
      <TitleHeading
        title="내정보"
        description="사용자 정보 화면입니다."
        className="mb-[0.438rem]"
      />
      <div className="flex items-start gap-[0.625rem]">
        <form
          onSubmit={methods.handleSubmit(onSubmit)}
          className="w-[18.75rem] 1400:w-[21.875rem] 1500:w-[25rem] flex flex-col"
        >
          <Box className="mb-[0.313rem]">
            <h3 className="font-medium text-16">내정보</h3>
          </Box>
          <FormProvider {...methods}>
            <Box className="space-y-[0.938rem]">
              <TextField name="name" label="이름" readOnly />
              <TextField name="userId" label="아이디" readOnly />
              <div className="space-y-[0.313rem]">
                <PasswordTextField
                  label="비밀번호"
                  name="currentPassword"
                  placeholder="기존 비밀번호"
                  required
                  maxLength={20}
                  rules={{
                    pattern: {
                      value:
                        // eslint-disable-next-line no-useless-escape
                        /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])[A-Za-z\d!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]{8,20}$/,
                      message: "비밀번호가 일치하지 않습니다.",
                    },
                    validate: async (value) => {
                      if (value.length >= 4) {
                        const res = await checkPasswordMutation.mutateAsync({
                          id: user!.id,
                          password: value,
                        });
                        if (res.isCorrect === false) {
                          return "비밀번호가 일치하지 않습니다.";
                        }
                      }
                      return true;
                    },
                  }}
                />
                <PasswordTextField
                  name="password"
                  placeholder="새 비밀번호"
                  rules={
                    password
                      ? {
                          pattern: {
                            value:
                              // eslint-disable-next-line no-useless-escape
                              /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])[A-Za-z\d!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]{8,20}$/,
                            message:
                              "대/소문자 영문 + 숫자 + 특수기호 조합으로 8~20자 이내",
                          },
                        }
                      : {}
                  }
                  maxLength={20}
                />
                <PasswordTextField
                  name="passwordConfirm"
                  placeholder="새 비밀번호 확인"
                  required={!!password}
                  rules={{
                    validate: (value) =>
                      !value ||
                      value === password ||
                      "새 비밀번호가 일치하지 않습니다.",
                  }}
                  maxLength={20}
                />
              </div>
              <TextField
                name="phone"
                required
                label="휴대전화번호"
                number
                maxLength={11}
                rules={{
                  minLength: 11,
                }}
              />
              <TextField label="대성 아이디" name="daesungId" readOnly />
              <div>
                <Label>은행/계좌번호</Label>
                <div className="flex gap-[0.313rem]">
                  <Select
                    name="bank"
                    options={bankOptions}
                    required
                    border
                    size="big"
                    className="!w-[7.188rem]"
                  />
                  <TextField
                    name="bankNumber"
                    required
                    number
                    maxLength={35}
                    className="flex-1"
                  />
                </div>
              </div>
            </Box>
            <Button
              type="submit"
              className="mt-[1.875rem] w-[10.688rem] self-end"
            >
              정보 수정
            </Button>
          </FormProvider>
        </form>
        <div className="flex-1 flex flex-col gap-[0.625rem]">
          <Board title="현재 등급" className="size-[14.75rem]">
            <div className="flex flex-col items-center gap-[1.125rem] mt-[2.938rem]">
              <strong className="text-48 font-bold text-primary whitespace-nowrap">
                {user?.grade?.name}
              </strong>
              <span className="text-14 text-secondary">
                건당 {(user?.grade?.amount || 0).toLocaleString()}원
              </span>
            </div>
          </Board>
          <Box className="!pb-0 max-w-[100rem]">
            <div className="flex justify-between pb-[0.625rem] gap-5 border-b border-gray">
              <div className="flex gap-[0.625rem]">
                <h3 className="font-medium text-18 shrink-0">
                  월간 답변 및 예상 급여
                </h3>
              </div>
              <span className="text-14 text-secondary shrink-0">
                {`기간 : ${format(user!.startMonth, "yyyy-MM-dd")} ~ ${format(endOfMonth(user!.startMonth!), "yyyy-MM-dd")}`}
              </span>
            </div>
            <ul className="flex pt-[1.625rem] pb-[2rem] -mx-[1.875rem]">
              {values.map(
                (
                  { label, value, description, unit, className, onClick },
                  index,
                ) => (
                  <li
                    key={label}
                    className={clsx(
                      "relative flex flex-col items-center gap-[1.938rem] pt-[0.438rem] pb-[1.563rem]",
                      {
                        "flex-1 after:absolute after:h-full after:w-px after:right-0 after:bg-gray":
                          index !== values.length - 1,
                        "w-[15rem] 1500:w-[17.25rem]":
                          index === values.length - 1,
                      },
                    )}
                  >
                    <span className="text-14 text-secondary">
                      {label || "\u00A0"}
                    </span>
                    <div className="flex flex-col">
                      <span className="text-16 font-medium text-secondary">
                        {onClick ? (
                          <button
                            type="button"
                            onClick={onClick}
                            className={clsx(
                              "relative text-36 font-bold inline-block after:w-full after:absolute after:left-0 after:-bottom-1 after:h-[2px] after:bg-black",
                              className,
                            )}
                          >
                            {value || "\u00A0"}
                          </button>
                        ) : (
                          <strong
                            className={clsx("text-36 font-bold", className)}
                          >
                            {value || "\u00A0"}
                          </strong>
                        )}{" "}
                        {value ? unit : ""}
                      </span>
                      {description && (
                        <span className="absolute bottom-0 text-14 text-secondary">
                          {description}
                        </span>
                      )}
                    </div>
                  </li>
                ),
              )}
            </ul>
          </Box>
        </div>
      </div>
    </main>
  );
};

export default MyInfoPage;
